/**
 * localStorage stamp for the background experiment-generation job.
 * Written on site-add so research / campaign screens can poll immediately.
 */

const GENERATION_PENDING_KEY = "humblytics:generation:pending:v1"
const STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000

export type GenerationPendingRecord = {
  propertyId: string
  domain: string
  goal: string | null
  siteAddedAt: string
  verified?: boolean
  jobId?: string
}

export function readGenerationPending(): GenerationPendingRecord | null {
  try {
    const raw = localStorage.getItem(GENERATION_PENDING_KEY)
    if (!raw) return null
    const record = JSON.parse(raw) as GenerationPendingRecord
    if (!record?.propertyId) return null
    if (record.siteAddedAt) {
      const age = Date.now() - new Date(record.siteAddedAt).getTime()
      if (age > STALE_AFTER_MS) {
        localStorage.removeItem(GENERATION_PENDING_KEY)
        return null
      }
    }
    return record
  } catch {
    return null
  }
}

export function stampGenerationPending(record: GenerationPendingRecord): void {
  try {
    localStorage.setItem(GENERATION_PENDING_KEY, JSON.stringify(record))
  } catch {
    // Best-effort
  }
}

/**
 * Stamp whether the tracker verified, without disturbing the rest of the
 * record. The tracker step runs after research now, so the pending record is
 * already on disk by the time this is known.
 */
export function markGenerationVerified(propertyId: string, verified: boolean): void {
  const record = readGenerationPending()
  if (!record || record.propertyId !== propertyId) return
  stampGenerationPending({ ...record, verified })
}

export function clearGenerationPending(): void {
  try {
    localStorage.removeItem(GENERATION_PENDING_KEY)
  } catch {
    // Best-effort
  }
}

export { GENERATION_PENDING_KEY }
